import {
  Location_userAccount$data,
  Location_userAccount$key,
} from '#app/__generated__/relay/Location_userAccount.graphql.js';
import {
  Location_userProfile$data,
  type Location_userProfile$key,
} from '#app/__generated__/relay/Location_userProfile.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { Link } from '#app/components/Link/index.js';
import { LocationText } from '#app/components/LocationText/LocationText.js';
import { TextInput } from '#app/components/TextInput/TextInput.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useEditSidebar } from '#app/features/profile/hooks/useEditSidebar.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useForm } from '#app/hooks/useForm.js';
import { useTrackInput } from '#app/hooks/useTrackInput.js';
import { ExcludeRelayFutureAddedValue } from '#app/types/utils.js';
import {
  EditableActions,
  ValidationRow,
  ValidationText,
} from './InlineEditing.styles.js';
import { InlineEditingEmptyPlaceholder } from './InlineEditingEmptyPlaceholder.js';
import { dayjs } from '@contra/date';
import { ContraVerificationStatus } from '@contra/graphql-types';
import { EditIcon, LocationIcon, LockOutlineIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { ErrorMessage } from '@hookform/error-message';
import { lazy, useCallback, useState } from 'react';
import { useController } from 'react-hook-form';
import { css, styled } from 'styled-components';
import { z } from 'zod';

const LocationInput = lazy(async () => {
  return {
    default: (await import('#app/components/LocationInput/LocationInput.js'))
      .LocationInput,
  };
});

const LocationLayout = styled.div`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    display: inline-flex;
    align-items: center;

    & > svg path {
      fill: ${theme.colorsV2.black};
      fill-opacity: 100%;
    }

    & > * + * {
      margin-left: 12px;
    }
  `}
`;

// We really need to improve the extensibility of this component
// https://linear.app/contra/issue/PLT-782/improve-extensibility-of-location-field
const LocationForm = styled.div`
  width: 100%;

  & .locationInputCombobox {
    min-height: auto;

    background: transparent;
    padding: 0;

    & > label {
      display: none;
    }

    & > div {
      padding: 0;

      & > svg {
        margin-left: 8px !important;
      }

      & > input {
        height: unset;
      }
    }
  }
`;

const LockedLocationLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  & > button {
    margin-top: 8px;
    margin-left: auto;
  }
`;

const LockedLocationCallout = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.gray05};
    border-radius: 10px;
    padding: 12px;

    & > span {
      color: ${theme.colorsV2.gray70};
      line-height: 20px;
      font-size: 14px;
      letter-spacing: 0.15px;
    }
  `}
`;

const DisabledLocation = styled(LocationIcon)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray40};
  `}
`;

const SupportLink = styled.a`
  ${({ theme }) => css`
    text-decoration: underline;

    &:hover {
      color: ${theme.colorsV2.gray70};
    }
  `}
`;

const VerifyLink = styled(Link)`
  ${({ theme }) => css`
    text-decoration: underline;

    &:hover {
      color: ${theme.colorsV2.gray70};
    }
  `}
`;

type LocationProps = {
  readonly disableEditing?: boolean;
  readonly userAccountRef?: Location_userAccount$key | null;
  readonly userProfileRef: Location_userProfile$key;
};

const HIDE_VERIFY_IDENTITY_MESSAGE_STATUSES: Array<
  ExcludeRelayFutureAddedValue<ContraVerificationStatus>
> = ['VERIFIED', 'NEEDS_PAYOUT_METHOD'];

const LocationDisclaimer = ({
  locationDetails,
  verificationStatus,
}: {
  readonly locationDetails: Location_userAccount$data['locationDetails'];
  readonly verificationStatus?: Location_userProfile$data['verificationStatus'];
}) => {
  const currentUser = useCurrentUser();

  const verifyIdentity =
    verificationStatus &&
    verificationStatus !== '%future added value' &&
    !HIDE_VERIFY_IDENTITY_MESSAGE_STATUSES.includes(verificationStatus) ? (
      <>
        <VerifyLink
          to={getRoute('/@userType/wallet', {
            args: {
              userType:
                currentUser?.selectedUserType === 'CLIENT'
                  ? 'client'
                  : 'independent',
            },
          })}
        >
          verify your identity
        </VerifyLink>{' '}
        or
      </>
    ) : null;

  const contactSupport = (
    <>
      <SupportLink href="mailto:hello@contra.com">contact support</SupportLink>{' '}
    </>
  );

  switch (locationDetails?.source) {
    case 'AUTO_DETECTED_ON_SIGNUP':
      return (
        <span>
          We’ve detected your location based on your IP at the time of sign-up.
          If this is incorrect, {verifyIdentity} {contactSupport} with proof of
          location to update.
        </span>
      );

    case 'UPDATED_AFTER_VERIFICATION':
      return (
        <span>
          We've detected your location based on your ID verification. If this
          changes or is incorrect, {contactSupport} with proof of location to
          update.
        </span>
      );

    case 'UPDATED_BY_SUPPORT':
      return (
        <span>
          Your location was updated by Contra on{' '}
          {dayjs(locationDetails.updatedAt).format('MMM D, YYYY')}. If this
          changes or is incorrect, {verifyIdentity} {contactSupport}
          with proof of location to update.
        </span>
      );

    case 'MANUALLY_SET_BY_USER':
    default:
      return (
        <span>
          If this changes or is incorrect, {verifyIdentity} {contactSupport}
          with proof of location to update.
        </span>
      );
  }
};

export const Location = ({
  disableEditing = false,
  userAccountRef,
  userProfileRef,
}: LocationProps) => {
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const toast = useToast();

  const userAccount = useFragment(
    graphql`
      fragment Location_userAccount on UserAccount {
        locationDetails {
          source
          updatedAt
        }
      }
    `,
    userAccountRef ?? null,
  );

  const user = useFragment(
    graphql`
      fragment Location_userProfile on UserProfile {
        profileDisplayType
        location
        locationGooglePlaceId
        visitorCanEdit
        verificationStatus
      }
    `,
    userProfileRef,
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      locationGooglePlaceId: user.locationGooglePlaceId ?? '',
    },
    mode: 'onChange',
    schema: z.object({
      locationGooglePlaceId: z
        .string({
          invalid_type_error: 'You must provide a location.',
          required_error: 'You must provide a location.',
        })
        .trim()
        .nonempty('You must provide a location.'),
    }),
  });

  const { track } = useAnalytics();

  const { track: trackInput } = useTrackInput({
    location_google_place_id: watch('locationGooglePlaceId'),
  });

  const initialPlaceId = user.locationGooglePlaceId ?? undefined;

  const { field } = useController({
    control,
    defaultValue: initialPlaceId,
    name: 'locationGooglePlaceId',
  });

  const isInternalProfile = ['BLOG', 'RESOURCE'].includes(
    user.profileDisplayType,
  );

  const location = isInternalProfile ? 'Remote' : user.location;

  const { updateLocation } = useEditSidebar();

  const cancelEditing = useCallback(() => {
    setIsInlineEditing(false);
  }, []);

  const onEditLocation = useCallback(
    ({
      locationGooglePlaceId: updatedLocationGooglePlaceId,
    }: {
      locationGooglePlaceId: string;
    }) => {
      trackInput();
      updateLocation(updatedLocationGooglePlaceId, () => {
        cancelEditing();
        toast.success('Location updated');
      });
    },
    [cancelEditing, toast, trackInput, updateLocation],
  );

  const handleOnClickEditLocation = useCallback(() => {
    if (!isInlineEditing) {
      track('button', {
        action: 'tapped',
        button_name: 'identity_inline_edit_location',
      });
      setIsInlineEditing(true);
    }
  }, [isInlineEditing, track]);

  if (user.visitorCanEdit && !disableEditing) {
    if (isInlineEditing) {
      if (location) {
        return (
          <LockedLocationLayout>
            <TextInput
              ariaLabel="Locked location"
              disabled
              name="Locked location"
              prefix={<DisabledLocation size={16} />}
              size="small"
              suffix={{
                icon: LockOutlineIcon,
                label: 'Locked',
              }}
              value={location}
              variant="text"
            />
            {userAccount ? (
              <LockedLocationCallout>
                <LocationDisclaimer
                  locationDetails={userAccount.locationDetails ?? null}
                  verificationStatus={user.verificationStatus}
                />
              </LockedLocationCallout>
            ) : null}
            <Button
              onClick={() => {
                setIsInlineEditing(false);
              }}
              size="xsmall"
              variant="V2Primary"
            >
              Done
            </Button>
          </LockedLocationLayout>
        );
      }

      return (
        <LocationForm>
          <LocationInput
            fallback={
              <LocationLayout>
                <LocationIcon size={16} />
                <LocationText location={location} />
              </LocationLayout>
            }
            hasError={errors?.locationGooglePlaceId !== undefined}
            initialPlaceId={initialPlaceId}
            onSelection={(suggestion) => {
              field.onChange(suggestion?.placeId ?? '');
            }}
            placeholder={location ?? undefined}
            types={['CITY']}
          />
          <ValidationRow>
            <ValidationText
              hasError={errors?.locationGooglePlaceId !== undefined}
            >
              <ErrorMessage
                errors={errors}
                name="locationGooglePlaceId"
              />
            </ValidationText>
          </ValidationRow>
          <EditableActions>
            <Button
              name="cancel"
              onClick={cancelEditing}
              size="xsmall"
              variant="V2TertiaryOutline"
            >
              Cancel
            </Button>
            <Button
              name="save"
              onClick={handleSubmit(onEditLocation)}
              size="xsmall"
              variant="V2Primary"
            >
              Save
            </Button>
          </EditableActions>
        </LocationForm>
      );
    } else {
      if (!location) {
        return (
          <InlineEditingEmptyPlaceholder
            label="Add a location"
            onClick={handleOnClickEditLocation}
          />
        );
      }

      return (
        <InlineEditContainer
          alignCenter
          label="Edit your Location"
          onClick={handleOnClickEditLocation}
          visitorCanEdit={user.visitorCanEdit}
        >
          <LocationLayout>
            <LocationIcon size={16} />
            <LocationText location={location} />
          </LocationLayout>
          <EditIcon size={16} />
        </InlineEditContainer>
      );
    }
  } else {
    return (
      <LocationLayout>
        <LocationIcon size={16} />
        <LocationText location={location} />
      </LocationLayout>
    );
  }
};
